import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Bell, BellOff, BellRing, Loader2, Check, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Push notification states
const PUSH_STATES = {
    LOADING: 'loading',
    UNSUPPORTED: 'unsupported',
    DENIED: 'denied',
    INACTIVE: 'inactive',      // Browser has no subscription
    ACTIVE: 'active',          // Browser has subscription AND backend has it
    SYNCING: 'syncing',        // Syncing with backend
    ERROR: 'error'
};

const PushNotificationSettings = ({ compact = false }) => {
    const { token, isAuthenticated } = useAuth();
    const [state, setState] = useState(PUSH_STATES.LOADING);
    const [error, setError] = useState(null);
    const [deviceCount, setDeviceCount] = useState(0);

    // Check push notification support and current state
    const checkState = useCallback(async () => {
        // Check browser support
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            setState(PUSH_STATES.UNSUPPORTED);
            return;
        }

        // Check permission
        if (Notification.permission === 'denied') {
            setState(PUSH_STATES.DENIED);
            return;
        }

        // Check for existing subscription
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            
            if (!subscription) {
                setState(PUSH_STATES.INACTIVE);
                return;
            }

            // We have a local subscription - verify it exists on backend
            if (isAuthenticated && token) {
                try {
                    const response = await fetch(`${API_URL}/api/notifications/subscription-status`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        setDeviceCount(data.device_count || 0);
                        
                        if (data.has_subscription) {
                            setState(PUSH_STATES.ACTIVE);
                        } else {
                            // Browser has subscription but backend doesn't - need to sync
                            console.log('Local subscription exists but not in backend, syncing...');
                            await syncSubscription(subscription);
                        }
                    } else {
                        // Backend check failed, assume active if local subscription exists
                        setState(PUSH_STATES.ACTIVE);
                    }
                } catch (err) {
                    console.error('Error checking backend status:', err);
                    // Fallback: assume active if local subscription exists
                    setState(PUSH_STATES.ACTIVE);
                }
            } else {
                // Not authenticated, can't check backend
                setState(PUSH_STATES.ACTIVE);
            }
        } catch (err) {
            console.error('Error checking subscription:', err);
            setState(PUSH_STATES.ERROR);
            setError('Eroare la verificarea stării notificărilor');
        }
    }, [isAuthenticated, token]);

    // Sync local subscription to backend
    const syncSubscription = async (subscription) => {
        if (!token) {
            console.error('[Push] No auth token available');
            return false;
        }
        
        setState(PUSH_STATES.SYNCING);
        
        try {
            const p256dhKey = subscription.getKey('p256dh');
            const authKey = subscription.getKey('auth');
            
            if (!p256dhKey || !authKey) {
                throw new Error('Invalid subscription keys');
            }
            
            const subscribeUrl = `${API_URL}/api/notifications/subscribe`;
            console.log('[Push] Subscribing to:', subscribeUrl);
            
            const bodyData = {
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(p256dhKey))),
                    auth: btoa(String.fromCharCode.apply(null, new Uint8Array(authKey)))
                }
            };
            console.log('[Push] Subscription endpoint:', subscription.endpoint.substring(0, 50) + '...');
            
            const response = await fetch(subscribeUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(bodyData)
            });

            console.log('[Push] Subscribe response status:', response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('[Push] Subscribe error response:', errorText);
                let errorData = {};
                try { errorData = JSON.parse(errorText); } catch {}
                throw new Error(errorData.detail || `Server returned ${response.status}`);
            }
            
            const result = await response.json();
            console.log('[Push] Subscribe success:', result);

            setState(PUSH_STATES.ACTIVE);
            return true;
        } catch (err) {
            console.error('[Push] Sync error:', err);
            setState(PUSH_STATES.ERROR);
            setError(err.message);
            return false;
        }
    };

    // Subscribe to push notifications
    const subscribe = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru notificări');
            return;
        }

        setState(PUSH_STATES.SYNCING);
        setError(null);

        try {
            // Request permission
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                setState(PUSH_STATES.DENIED);
                toast.error('Ai blocat notificările. Activează-le din setările browserului.');
                return;
            }

            // Get VAPID key with timeout and retry
            console.log('[Push] Fetching VAPID key from:', `${API_URL}/api/notifications/vapid-public-key`);
            
            let vapidResponse;
            let retries = 2;
            
            while (retries > 0) {
                try {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
                    
                    vapidResponse = await fetch(`${API_URL}/api/notifications/vapid-public-key`, {
                        signal: controller.signal,
                        headers: {
                            'Accept': 'application/json'
                        }
                    });
                    
                    clearTimeout(timeoutId);
                    
                    if (vapidResponse.ok) break;
                } catch (fetchError) {
                    console.error('[Push] Fetch attempt failed:', fetchError.message);
                    retries--;
                    if (retries === 0) throw fetchError;
                    await new Promise(r => setTimeout(r, 1000)); // Wait 1s before retry
                }
            }
            
            console.log('[Push] VAPID response status:', vapidResponse?.status);
            
            if (!vapidResponse || !vapidResponse.ok) {
                const errorText = vapidResponse ? await vapidResponse.text() : 'No response';
                console.error('[Push] VAPID error response:', errorText);
                throw new Error('Nu s-a putut obține cheia serverului. Verifică conexiunea la internet.');
            }
            const vapidData = await vapidResponse.json();
            const { publicKey } = vapidData;
            console.log('[Push] VAPID key received:', publicKey ? `${publicKey.substring(0, 20)}...` : 'null');
            
            if (!publicKey) {
                throw new Error('Cheia serverului nu este configurată');
            }

            // Register service worker
            const registration = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;

            // Subscribe
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            });

            // Save to backend
            const synced = await syncSubscription(subscription);
            
            if (synced) {
                toast.success('Notificările au fost activate!');
                // Refresh device count
                checkState();
            } else {
                throw new Error('Nu s-a putut salva subscripția în server');
            }
        } catch (err) {
            console.error('Subscribe error:', err);
            setState(PUSH_STATES.ERROR);
            
            if (err.message.includes('permission')) {
                setError('Notificările sunt blocate în browser');
                toast.error('Ai blocat notificările. Activează-le din setările browserului.');
            } else {
                setError(err.message);
                toast.error(err.message || 'Eroare la activarea notificărilor');
            }
        }
    };

    // Unsubscribe from push notifications
    const unsubscribe = async () => {
        setState(PUSH_STATES.SYNCING);

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            
            if (subscription) {
                // Unsubscribe locally
                await subscription.unsubscribe();
                
                // Remove from backend
                if (token) {
                    await fetch(`${API_URL}/api/notifications/subscribe`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ endpoint: subscription.endpoint })
                    });
                }
            }

            setState(PUSH_STATES.INACTIVE);
            toast.success('Notificările au fost dezactivate');
        } catch (err) {
            console.error('Unsubscribe error:', err);
            setState(PUSH_STATES.ERROR);
            setError(err.message);
            toast.error('Eroare la dezactivarea notificărilor');
        }
    };

    // Force resync subscription
    const resync = async () => {
        setState(PUSH_STATES.SYNCING);
        setError(null);

        try {
            const registration = await navigator.serviceWorker.ready;
            let subscription = await registration.pushManager.getSubscription();
            
            if (!subscription) {
                // Need to create new subscription
                const vapidResponse = await fetch(`${API_URL}/api/notifications/vapid-public-key`);
                const { publicKey } = await vapidResponse.json();
                
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicKey)
                });
            }

            const synced = await syncSubscription(subscription);
            
            if (synced) {
                toast.success('Subscripție reînregistrată cu succes!');
                checkState();
            }
        } catch (err) {
            console.error('Resync error:', err);
            setState(PUSH_STATES.ERROR);
            setError(err.message);
            toast.error('Eroare la reînregistrare: ' + err.message);
        }
    };

    // Send test notification
    const sendTest = async () => {
        if (!token) {
            toast.error('Trebuie să fii autentificat');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/notifications/test-push`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            
            if (response.ok) {
                if (data.sent_count > 0) {
                    toast.success(`Notificare trimisă la ${data.sent_count} dispozitiv(e)!`);
                } else {
                    toast.error('Niciun dispozitiv înregistrat. Apasă "Reînregistrează".');
                }
            } else {
                toast.error(data.detail || 'Eroare la trimiterea notificării de test');
            }
        } catch (err) {
            toast.error('Eroare la trimiterea notificării de test');
        }
    };

    // Toggle handler
    const handleToggle = () => {
        if (state === PUSH_STATES.ACTIVE) {
            unsubscribe();
        } else if (state === PUSH_STATES.INACTIVE || state === PUSH_STATES.ERROR) {
            subscribe();
        }
    };

    // Initialize
    useEffect(() => {
        // Pre-register service worker
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/sw.js').catch(() => {});
        }
        
        checkState();
    }, [checkState]);

    // Re-check when auth changes
    useEffect(() => {
        if (isAuthenticated && token) {
            checkState();
        }
    }, [isAuthenticated, token, checkState]);

    // Helper to convert VAPID key
    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    // Compact version for navbar/header
    if (compact) {
        if (state === PUSH_STATES.UNSUPPORTED || state === PUSH_STATES.DENIED) {
            return (
                <Button
                    variant="ghost"
                    size="sm"
                    disabled
                    className="relative text-muted-foreground opacity-50"
                    title={state === PUSH_STATES.UNSUPPORTED ? 'Browserul nu suportă notificări' : 'Notificările sunt blocate'}
                    data-testid="push-notifications-toggle"
                >
                    <BellOff className="w-5 h-5" />
                </Button>
            );
        }

        return (
            <Button
                variant="ghost"
                size="sm"
                onClick={handleToggle}
                disabled={state === PUSH_STATES.LOADING || state === PUSH_STATES.SYNCING}
                className={`relative ${state === PUSH_STATES.ACTIVE ? 'text-primary' : 'text-muted-foreground'}`}
                title={state === PUSH_STATES.ACTIVE ? 'Dezactivează notificările' : 'Activează notificările'}
                data-testid="push-notifications-toggle"
            >
                {state === PUSH_STATES.LOADING || state === PUSH_STATES.SYNCING ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                ) : state === PUSH_STATES.ACTIVE ? (
                    <BellRing className="w-5 h-5" />
                ) : (
                    <Bell className="w-5 h-5" />
                )}
                {state === PUSH_STATES.ACTIVE && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" />
                )}
            </Button>
        );
    }

    // Unsupported state
    if (state === PUSH_STATES.UNSUPPORTED) {
        return (
            <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                        <BellOff className="w-5 h-5 text-orange-500" />
                        <p className="text-sm text-orange-700">
                            Browserul tău nu suportă notificări push
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Denied state
    if (state === PUSH_STATES.DENIED) {
        return (
            <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                        <BellOff className="w-5 h-5 text-red-500 mt-0.5" />
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-red-700">
                                Notificările sunt blocate
                            </p>
                            <p className="text-xs text-red-600">
                                Pentru a primi alerte, trebuie să activezi notificările din browser:
                            </p>
                            <ol className="text-xs text-red-600 list-decimal list-inside space-y-1">
                                <li>Click pe iconița 🔒 din bara de adrese</li>
                                <li>Găsește "Notificări" sau "Notifications"</li>
                                <li>Schimbă din "Blocat" în "Permite"</li>
                                <li>Reîncarcă pagina</li>
                            </ol>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Full settings card
    const isLoading = state === PUSH_STATES.LOADING || state === PUSH_STATES.SYNCING;
    const isActive = state === PUSH_STATES.ACTIVE;

    return (
        <Card data-testid="push-notification-settings">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Notificări Push
                </CardTitle>
                <CardDescription>
                    Primește alerte când licitațiile tale expiră sau când cineva te depășește
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="font-medium">Activează notificările</p>
                        <p className="text-sm text-muted-foreground">
                            Vei fi notificat cu 1 oră, 30 min, 15 min, 5 min și 1 min înainte de finalizare
                        </p>
                    </div>
                    <Switch
                        checked={isActive}
                        onCheckedChange={handleToggle}
                        disabled={isLoading}
                        data-testid="push-notifications-switch"
                    />
                </div>

                {/* Error state */}
                {state === PUSH_STATES.ERROR && error && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-red-700">{error}</p>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto p-0 text-red-600"
                                    onClick={() => { setError(null); checkState(); }}
                                >
                                    Încearcă din nou
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Active state actions */}
                {isActive && (
                    <div className="pt-4 border-t space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-green-600">
                                <Check className="w-4 h-4" />
                                <span>Notificările sunt active</span>
                                {deviceCount > 0 && (
                                    <span className="text-muted-foreground">
                                        ({deviceCount} {deviceCount === 1 ? 'dispozitiv' : 'dispozitive'})
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={resync}
                                    disabled={isLoading}
                                    title="Reînregistrează subscripția dacă nu primești notificări"
                                    data-testid="push-resync-btn"
                                >
                                    {isLoading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="w-4 h-4" />
                                    )}
                                    <span className="ml-2 hidden sm:inline">Reînregistrează</span>
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={sendTest}
                                    disabled={isLoading}
                                    data-testid="push-test-btn"
                                >
                                    Trimite test
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <p className="text-sm text-blue-700">
                        <strong>Tip:</strong> Vei primi notificări pentru:
                    </p>
                    <ul className="mt-2 text-sm text-blue-600 space-y-1">
                        <li>• Licitații la care participi și se apropie de final</li>
                        <li>• Când cineva te depășește cu o ofertă</li>
                        <li>• Când câștigi o licitație</li>
                    </ul>
                </div>
            </CardContent>
        </Card>
    );
};

export default PushNotificationSettings;
